import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, withModifiers as _withModifiers, createBlock as _createBlock, renderSlot as _renderSlot, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "e-date-picker__wrapper" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 0,
  class: "e-date-picker__required"
}
const _hoisted_4 = { class: "e-date-picker__value" }
const _hoisted_5 = {
  key: 0,
  class: "e-date-picker__side"
}

import { ref, computed, inject, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import useDevice from '@/hooks/useDevice';
import utils from '@/utils';
import { FormDateEmits, FormDateProps, FormDateRef } from '@/types/form/form-date';
import { DynamicFormContext, DynamicFormFieldValidateResult } from '@/types/form/dynamic-form';
import { ModalRef } from '@/types/elements/modal';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import FormElMsg from '@/form/other/FormElMsg.vue';
import DatepickerPopup from '@/form/FormDatepicker/DatepickerPopup.vue';

const FIELD_TYPE = 'form-date';

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    item: {}
  },
  emits: ["change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;
const formContext = inject<DynamicFormContext>('formContext')!;
const { deviceState } = useDevice();

const rootEl = ref<HTMLDivElement | null>(null);
const datepickerPopupRef = ref<any>(null);
const datepickerPopupRefMobile = ref<any>(null);
const dateModalRef = ref<ModalRef>();

const value = ref<string>('');
const selectedDate = ref<string>('');
const isRequired = ref<boolean>(props.item?.fields?.required?.value ?? false);
const isDisabled = ref<boolean>(false);
const isVisible = ref<boolean>(true);
const focused = ref<boolean>(false);
const dateActive = ref<boolean>(false);
const msg = ref<FormElMsgData>();
let blurTimeout: any = null;

const fields = computed(() => props.item?.fields);
const label = computed(() => fields.value?.label?.value || '');
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(
  () => fields.value?.fieldName?.value || fields.value?.dataName?.value || ''
);
const weeksData = computed(() => fields.value?.weeks?.fields?.data);
const monthsData = computed(() => fields.value?.month?.fields?.data);
const startDataOverride = ref<number | null>(null);
const endDataOverride = ref<number | null>(null);
const startData = computed(() => startDataOverride.value ?? fields.value?.startData?.value);
const endData = computed(() => endDataOverride.value ?? fields.value?.endData?.value);
const deviceType = computed(() => deviceState.deviceType);
const hasValue = computed(() => !utils.obj.isNoE(value.value));

const rootClasses = computed(() => [
  {
    focus: focused.value,
    'has-value': hasValue.value,
    'has-error': msg.value?.type === 'error',
    disabled: isDisabled.value,
  },
]);

const toggleDate = () => {
  if (isDisabled.value) return;
  focused.value = true;
  dateActive.value = !dateActive.value;
};

const onDateBlur = () => {
  if (blurTimeout) clearTimeout(blurTimeout);
  blurTimeout = setTimeout(() => {
    if (deviceState.deviceType.includes('desktop')) {
      dateActive.value = false;
    }
  }, 50);
};

const onDateOptionClick = (date: string) => {
  dateActive.value = false;
  focused.value = false;
  selectedDate.value = date;
  value.value = date;
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, date, { date, text: date });
};

const onClear = (e: MouseEvent) => {
  e.stopPropagation();
  if (isDisabled.value) return;
  dateActive.value = false;
  focused.value = false;
  selectedDate.value = '';
  value.value = '';
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, null, { date: null, text: '' });
};

const checkMobileMotion = async () => {
  if (!deviceState.deviceType.includes('desktop')) {
    if (dateActive.value) {
      await dateModalRef.value?.open();
      await nextTick();
    } else {
      await dateModalRef.value?.close();
    }
  } else {
    if (dateModalRef.value) {
      await dateModalRef.value?.close();
    }
  }
};

const setRequired = (required: boolean) => {
  isRequired.value = required;
};
const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};
const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};
const updateValue = (newValue?: string | null) => {
  value.value = newValue || '';
  selectedDate.value = newValue || '';
};
const setStartData = (newValue?: number | null) => {
  startDataOverride.value = newValue ?? null;
};
const setEndData = (newValue?: number | null) => {
  endDataOverride.value = newValue ?? null;
};
const getValue = () => value.value;

const validate = async (): Promise<DynamicFormFieldValidateResult> => {
  const result: DynamicFormFieldValidateResult = {
    value: value.value,
    valid: true,
    message: '',
  };
  if (!isVisible.value || !fields.value) return result;
  if (isRequired.value && utils.obj.isNoE(value.value)) {
    result.valid = false;
    result.message = fields.value?.requiredMsg?.value || 'This field is required';
    msg.value = { type: 'error', message: result.message };
    return result;
  }
  msg.value = undefined;
  return result;
};

const getRefData = (): FormDateRef => ({
  el: rootEl.value,
  type: FIELD_TYPE,
  name: fieldName.value,
  id: props.item?.id ?? '',
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  setStartData,
  setEndData,
  getValue,
  validate,
});

watch(dateActive, () => {
  checkMobileMotion();
});

watch(
  () => deviceState.deviceType,
  () => {
    checkMobileMotion();
  }
);

watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);

watch(
  () => props.item?.fields?.required?.value,
  (val) => {
    isRequired.value = val ?? false;
  }
);

onMounted(() => {
  formContext.regFormEl(getRefData());
});

onBeforeUnmount(() => {
  if (blurTimeout) clearTimeout(blurTimeout);
  formContext.unRegFormEl(getRefData());
});

__expose(getRefData());

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_modal = _resolveComponent("modal")!

  return (isVisible.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(['e-date-picker', ...rootClasses.value]),
        ref_key: "rootEl",
        ref: rootEl
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", {
            class: _normalizeClass(["e-date-picker__main", [{ active: deviceType.value.includes('desktop') && dateActive.value }]]),
            onClick: toggleDate,
            tabindex: "0"
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["e-date-picker__placeholder", [{ 'as-label': dateActive.value || hasValue.value }]])
            }, [
              _createElementVNode("span", {
                class: "e-date-picker__label",
                innerHTML: dateActive.value || hasValue.value ? label.value : _unref(utils).string.ifEmpty(placeholder.value, label.value)
              }, null, 8, _hoisted_2),
              (isRequired.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
                : _createCommentVNode("", true)
            ], 2),
            _createElementVNode("div", _hoisted_4, _toDisplayString(selectedDate.value), 1),
            _createVNode(_component_Icon, {
              name: "down",
              size: "xxs",
              class: "e-date-picker__down"
            }),
            (hasValue.value)
              ? (_openBlock(), _createBlock(_component_Icon, {
                  key: 0,
                  name: "close",
                  size: "xxs",
                  class: "e-date-picker__clear",
                  onClick: _withModifiers(onClear, ["stop"])
                }))
              : _createCommentVNode("", true)
          ], 2),
          (_ctx.$slots.default)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _renderSlot(_ctx.$slots, "default")
              ]))
            : _createCommentVNode("", true),
          _createVNode(_Transition, { name: "options-ani" }, {
            default: _withCtx(() => [
              (deviceType.value.includes('desktop') && dateActive.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "e-date-picker__day",
                    tabindex: "-1",
                    onBlur: onDateBlur
                  }, [
                    _createVNode(DatepickerPopup, {
                      ref_key: "datepickerPopupRef",
                      ref: datepickerPopupRef,
                      selected: selectedDate.value,
                      weeksText: weeksData.value,
                      monthsText: monthsData.value,
                      startData: startData.value,
                      endData: endData.value,
                      onChange: onDateOptionClick
                    }, null, 8, ["selected", "weeksText", "monthsText", "startData", "endData"]),
                    (deviceType.value.includes('desktop') && dateActive.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "overflowBg",
                          onClick: onDateBlur
                        }))
                      : _createCommentVNode("", true)
                  ], 32))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _createVNode(FormElMsg, { msg: msg.value }, null, 8, ["msg"]),
        _createVNode(_component_modal, {
          ref_key: "dateModalRef",
          ref: dateModalRef,
          class: "e-date-picker__phone-modal",
          animation: "bottom-slide-in",
          "fire-gtm": false,
          closable: ""
        }, {
          default: _withCtx(() => [
            _createVNode(DatepickerPopup, {
              ref_key: "datepickerPopupRefMobile",
              ref: datepickerPopupRefMobile,
              selected: selectedDate.value,
              weeksText: weeksData.value,
              monthsText: monthsData.value,
              startData: startData.value,
              endData: endData.value,
              onChange: onDateOptionClick
            }, null, 8, ["selected", "weeksText", "monthsText", "startData", "endData"])
          ]),
          _: 1
        }, 512)
      ], 2))
    : _createCommentVNode("", true)
}
}

})